(() => {
  'use strict';
  const CARD_WIDTH = 53.98;
  const CARD_HEIGHT = 85.60;
  const INITIAL_PPM = 96 / 25.4; // Starting size only; never treated as a detected screen DPI.
  const BUTTON_STEP = 0.025; // A visible 1.35 CSS px card-width change per tap.
  // Manufacturer screen specifications. A selected model is not device identification.
  // See README for sources and the different macOS/Android display-scaling formulas.
  const IPHONES = [
    {id:'iphone-18-pro',name:'iPhone 18 Pro',width:1206,height:2622,ppi:460},
    {id:'iphone-18-pro-max',name:'iPhone 18 Pro Max',width:1320,height:2868,ppi:460},
    {id:'iphone-17',name:'iPhone 17',width:1206,height:2622,ppi:460},
    {id:'iphone-17-pro',name:'iPhone 17 Pro',width:1206,height:2622,ppi:460},
    {id:'iphone-17-pro-max',name:'iPhone 17 Pro Max',width:1320,height:2868,ppi:460},
    {id:'iphone-air',name:'iPhone Air',width:1260,height:2736,ppi:460},
    {id:'iphone-17e',name:'iPhone 17e',width:1170,height:2532,ppi:460},
    {id:'iphone-16',name:'iPhone 16',width:1179,height:2556,ppi:460},
    {id:'iphone-16-plus',name:'iPhone 16 Plus',width:1290,height:2796,ppi:460},
    {id:'iphone-16-pro',name:'iPhone 16 Pro',width:1206,height:2622,ppi:460},
    {id:'iphone-16-pro-max',name:'iPhone 16 Pro Max',width:1320,height:2868,ppi:460},
    {id:'iphone-15',name:'iPhone 15',width:1179,height:2556,ppi:460},
    {id:'iphone-15-plus',name:'iPhone 15 Plus',width:1290,height:2796,ppi:460},
    {id:'iphone-15-pro',name:'iPhone 15 Pro',width:1179,height:2556,ppi:460},
    {id:'iphone-15-pro-max',name:'iPhone 15 Pro Max',width:1290,height:2796,ppi:460},
    {id:'iphone-14',name:'iPhone 14',width:1170,height:2532,ppi:460},
    {id:'iphone-14-plus',name:'iPhone 14 Plus',width:1284,height:2778,ppi:458},
    {id:'iphone-14-pro',name:'iPhone 14 Pro',width:1179,height:2556,ppi:460},
    {id:'iphone-14-pro-max',name:'iPhone 14 Pro Max',width:1290,height:2796,ppi:460}
  ].map(model => ({...model,kind:'iphone'}));
  const SAMSUNG = [
    {id:'galaxy-s26',name:'Samsung Galaxy S26',width:1080,height:2340,diagonal:159.3},
    {id:'galaxy-s26-plus',name:'Samsung Galaxy S26+',width:1440,height:3120,diagonal:169.1},
    {id:'galaxy-s26-ultra',name:'Samsung Galaxy S26 Ultra',width:1440,height:3120,diagonal:174.9},
    {id:'galaxy-s25',name:'Samsung Galaxy S25',width:1080,height:2340,diagonal:156.4},
    {id:'galaxy-s25-plus',name:'Samsung Galaxy S25+',width:1440,height:3120,diagonal:169.1},
    {id:'galaxy-s25-ultra',name:'Samsung Galaxy S25 Ultra',width:1440,height:3120,diagonal:174.2},
    {id:'galaxy-s24',name:'Samsung Galaxy S24',width:1080,height:2340,diagonal:156.4},
    {id:'galaxy-s24-plus',name:'Samsung Galaxy S24+',width:1440,height:3120,diagonal:169.1},
    {id:'galaxy-s24-ultra',name:'Samsung Galaxy S24 Ultra',width:1440,height:3120,diagonal:172.5}
  ].map(model => ({...model,kind:'samsung',physicalWidth:model.diagonal * model.width / Math.hypot(model.width,model.height)}));
  const MACBOOKS = [
    {id:'macbook-air-13',name:'MacBook Air 13-inch · M2–M5',width:2560,height:1664,ppi:224},
    {id:'macbook-air-15',name:'MacBook Air 15-inch · M2–M5',width:2880,height:1864,ppi:224},
    {id:'macbook-pro-14',name:'MacBook Pro 14-inch · M1–M5',width:3024,height:1964,ppi:254},
    {id:'macbook-pro-16',name:'MacBook Pro 16-inch · M1–M5',width:3456,height:2234,ppi:254},
    {id:'macbook-air-m1',name:'MacBook Air 13-inch · M1',width:2560,height:1600,ppi:227},
    {id:'macbook-pro-13',name:'MacBook Pro 13-inch · M1 / M2',width:2560,height:1600,ppi:227}
  ].map(model => ({...model,kind:'macbook',physicalWidth:model.width / model.ppi * 25.4}));
  const NS = 'http://www.w3.org/2000/svg';
  function initialise(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const get = selector => root.querySelector(selector);
    const card = get('[data-card]');
    const slider = get('[data-scale]');
    const confirm = get('[data-confirm]');
    const ruler = get('[data-ruler]');
    let ppm = INITIAL_PPM;
    let calibration = null;
    let measuring = false;
    let measurementMode = 'straps';
    let previewPresetId = null;
    const track = event => window.DelugsLugAnalytics?.track(event);
    const trackMeasurement = () => { if (measuring) track('lug_tool_' + measurementMode + '_view'); };
    const environment = () => ({
      dpr: window.devicePixelRatio || 1,
      zoom: window.visualViewport?.scale || 1,
      width: screen.width,
      height: screen.height,
      layoutWidth: document.documentElement.clientWidth,
      angle: screen.orientation?.angle ?? window.orientation ?? 0,
      availableLeft: screen.availLeft ?? 0,
      availableTop: screen.availTop ?? 0
    });
    const changed = (a, b) => Object.keys(a).some(key => Math.abs(a[key] - b[key]) > 0.015);
    const ua = navigator.userAgent;
    const touchMac = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
    const mobile = /iPhone|iPad|iPod|Android|Mobile/.test(ua) || touchMac || navigator.userAgentData?.mobile === true;
    const deviceSelect = get('[data-device]');
    const builtIn = get('[data-builtin-screen]');
    const models = mobile ? [...IPHONES, ...SAMSUNG] : MACBOOKS;
    root.dataset.deviceClass = mobile ? 'mobile' : 'desktop';
    get('[data-device-summary]').textContent = mobile ? 'Use your phone model' : 'Use your MacBook model';
    get('[data-device-label]').textContent = mobile ? 'Your phone model' : 'Your MacBook model';
    get('[data-device-intro]').textContent = mobile
      ? 'Choose your iPhone or Samsung Galaxy to set the scale automatically. Keep your phone upright and browser zoom at 100%.'
      : 'Choose your MacBook to set the scale automatically. Use its built-in screen in Chrome or Edge, with browser zoom at 100%.';
    get('[data-builtin-label]').hidden = mobile;
    const groups = mobile
      ? (/Android/.test(ua) ? [['Samsung Galaxy', SAMSUNG], ['Apple iPhone', IPHONES]] : [['Apple iPhone', IPHONES], ['Samsung Galaxy', SAMSUNG]])
      : [['MacBook', MACBOOKS]];
    groups.forEach(([label, entries]) => {
      const group = document.createElement('optgroup');
      group.label = label;
      entries.forEach(model => {
        const option = document.createElement('option');
        option.value = model.id;
        option.textContent = model.name;
        group.appendChild(option);
      });
      deviceSelect.appendChild(group);
    });
    function deviceEligibility() {
      const model = models.find(candidate => candidate.id === deviceSelect.value);
      if (!model) return {ok:false,message:'Select the model you’re using now.'};
      const e = environment();
      if (!measuring && Math.abs(card.getBoundingClientRect().width - CARD_WIDTH * ppm) >= 0.75) return {ok:false,message:'This page is being resized. Reset your zoom or use card calibration.'};
      if (Math.abs(e.zoom - 1) > 0.015) return {ok:false,message:'Zoom back out and set browser zoom to 100% before using a screen preset.'};
      if (model.kind === 'macbook') {
        // Safari does not consistently expose page zoom through devicePixelRatio.
        if (!/Macintosh|Mac OS X/.test(ua) || touchMac || !/Chrome\/|Edg\//.test(ua)) return {ok:false,message:'Use Chrome or Edge on your MacBook for this preset, or match a card below.'};
        if (!builtIn.checked) return {ok:false,message:'Confirm that you’re using this MacBook’s built-in screen.'};
        if (Math.abs(e.dpr - 2) > 0.015) return {ok:false,message:'Set browser zoom to 100% on your MacBook’s built-in Retina screen, or match a card below.'};
        const ratioMatches = Math.abs(e.width / e.height - model.width / model.height) < 0.006;
        if (!ratioMatches || e.width < model.width / 3 || e.width > model.width || e.angle % 180 !== 0) return {ok:false,message:'This screen’s proportions don’t match that MacBook. Check your model and built-in screen, or match a card below.'};
        // macOS scaled resolutions change logical screen width without changing DPR.
        return {ok:true,model,ppm:e.width / model.physicalWidth,message:'Ready to use the ' + model.name + ' screen preset.'};
      }
      // Chrome on iOS reports CriOS instead of Safari's Version token.
      // Both still have to pass the same native-screen, layout and zoom checks.
      if (model.kind === 'iphone' && (!/iPhone/.test(ua) || !/AppleWebKit\//.test(ua) || !/(?:Version\/[\d.]+.*Safari|CriOS\/[\d.]+)/.test(ua))) return {ok:false,message:'Use Safari or Chrome on the selected iPhone for this preset, or match a card below.'};
      if (model.kind === 'samsung' && (!/Android/.test(ua) || !/Chrome\/|SamsungBrowser\//.test(ua))) return {ok:false,message:'Use Chrome or Samsung Internet on the selected Galaxy for this preset, or match a card below.'};
      if (innerWidth > innerHeight || e.width > e.height || e.angle % 180 !== 0) return {ok:false,message:'Turn your phone upright to use this preset.'};
      if (Math.abs(e.layoutWidth - e.width) > 1.5) return {ok:false,message:'Set browser zoom to 100% and use the full screen before using this preset.'};
      const resolutions = model.kind === 'samsung' && model.width === 1440
        ? [[1440,3120],[1080,2340],[720,1560]] : [[model.width,model.height]];
      const tolerance = model.kind === 'samsung' ? Math.max(3,e.dpr) : 1;
      const matches = resolutions.some(([width,height]) => Math.abs(e.width * e.dpr - width) <= tolerance && Math.abs(e.height * e.dpr - height) <= tolerance);
      if (!matches) return {ok:false,message:'This screen doesn’t match that preset. Check your model and display settings, or match a card below.'};
      const pixelsPerMm = model.kind === 'iphone' ? model.ppi / (e.dpr * 25.4) : e.width / model.physicalWidth;
      return {ok:true,model,ppm:pixelsPerMm,message:'Screen settings match. Ready to use the ' + model.name + ' preset.'};
    }
    function updateDeviceStatus() {
      const result = deviceEligibility();
      get('[data-use-device]').disabled = !result.ok;
      get('[data-device-status]').textContent = result.ok && previewPresetId === result.model.id
        ? 'Scale set for ' + result.model.name + ' (' + scalePercentage(result.ppm) + '). Continue to measure, or check the card below.'
        : result.message;
      return result;
    }
    function previewDeviceScale() {
      if (deviceSelect.value) track('lug_tool_started');
      previewPresetId = null;
      const result = updateDeviceStatus();
      if (!result.ok) return;
      updateCard(result.ppm);
      previewPresetId = result.model.id;
      updateDeviceStatus();
    }
    const setNotice = message => {
      get('[data-notice]').textContent = message;
      get('[data-notice]').hidden = !message;
    };
    function cardFits() {
      const rect = card.getBoundingClientRect();
      const stage = get('[data-card-stage]');
      const padding = parseFloat(getComputedStyle(stage).paddingLeft) + parseFloat(getComputedStyle(stage).paddingRight);
      const controlsHeight = matchMedia('(max-width: 767px)').matches ? get('.dlr__controls').getBoundingClientRect().height : 0;
      const fits = rect.width <= stage.clientWidth - padding + 0.5 && rect.height <= (window.visualViewport?.height || innerHeight) - controlsHeight - 24;
      const untransformed = Math.abs(rect.width - CARD_WIDTH * ppm) < 0.75;
      confirm.disabled = !fits || !untransformed;
      get('[data-fit-warning]').hidden = fits && untransformed;
      if (!untransformed) get('[data-fit-warning]').textContent = 'This page is being resized. Reset your screen zoom and match your card again.';
      else get('[data-fit-warning]').textContent = 'The whole card must fit on your screen. Make it smaller, or use a larger screen if your physical card does not fit.';
      return fits && untransformed;
    }
    function scalePercentage(value) {
      return `${(value / INITIAL_PPM * 100).toFixed(1)}%`;
    }
    function updateCard(value) {
      ppm = Math.max(Number(slider.min), Math.min(Number(slider.max), value));
      slider.value = ppm.toFixed(3);
      card.style.width = `${CARD_WIDTH * ppm}px`;
      card.style.height = `${CARD_HEIGHT * ppm}px`;
      card.style.borderRadius = `${3.18 * ppm}px`;
      const percentage = scalePercentage(ppm);
      get('[data-scale-output]').textContent = percentage;
      slider.setAttribute('aria-valuetext', `${percentage} of starting card size`);
      get('[data-adjust="-1"]').disabled = ppm <= Number(slider.min);
      get('[data-adjust="1"]').disabled = ppm >= Number(slider.max);
      cardFits();
    }
    function adjustCard(value) {
      track('lug_tool_started');
      // Manual adjustment replaces the preset, so Continue cannot silently undo it.
      previewPresetId = null;
      deviceSelect.value = '';
      updateCard(value);
      updateDeviceStatus();
    }
    function svgElement(tag, attributes, text) {
      const element = document.createElementNS(NS, tag);
      Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
      if (text !== undefined) element.textContent = text;
      ruler.appendChild(element);
    }
    function drawRuler() {
      // Only labels spread out: the ruler ticks always retain their physical scale.
      const labelSpacing = Math.max(16, ppm);
      const margin = Math.max(32, 6 * labelSpacing + 16 - 20 * ppm);
      const width = 40 * ppm + margin * 2;
      const baseline = 140;
      const firstLabel = baseline + 56;
      const height = firstLabel + 6 * 28 + 14;
      ruler.replaceChildren();
      ruler.setAttribute('width', width);
      ruler.setAttribute('height', height);
      ruler.setAttribute('viewBox', `0 0 ${width} ${height}`);
      ruler.style.width = `${width}px`;
      ruler.style.height = `${height}px`;
      svgElement('title', {}, 'Calibrated lug width ruler, whole millimetres with individual labels from 14 to 26');
      svgElement('line', {x1:margin,y1:24,x2:margin,y2:baseline,stroke:'#062541','stroke-width':1.5,'data-zero':''});
      svgElement('text', {x:margin,y:16,'font-size':15,'font-weight':700,'text-anchor':'middle',fill:'#062541'}, '0');
      svgElement('line', {x1:margin,y1:baseline,x2:margin+40*ppm,y2:baseline,stroke:'#062541','stroke-width':1});
      const widthLabels = [];
      // Each measured width keeps the same colour from its tick to its number.
      // Numeric labels and positions remain usable without distinguishing colour.
      const widthColours = ['#005e9e','#a94214','#267345','#7c3894','#007b80','#b33661','#8a6500','#3041aa','#9c493f','#60701b','#7b3c6c','#126577','#b42335'];
      for (let mm = 0; mm <= 40; mm++) {
        const x = margin + mm * ppm;
        const commonWidth = mm >= 14 && mm <= 26;
        const major = mm % 5 === 0;
        const colour = commonWidth ? widthColours[mm - 14] : '#062541';
        const tickEnd = baseline + (major ? 18 : 12);
        svgElement('line', {x1:x,y1:baseline,x2:x,y2:tickEnd,stroke:colour,'stroke-width':commonWidth?Math.min(2,ppm*.6):major?1.2:0.9,'data-mm':mm});
        if (commonWidth) {
          const row = Math.abs(mm - 20);
          const y = firstLabel + 28 * row;
          const labelX = margin + 20 * ppm + (mm - 20) * labelSpacing;
          svgElement('line', {x1:x,y1:tickEnd,x2:labelX,y2:y-17,stroke:colour,'stroke-width':Math.min(1.5,ppm*.5),'data-width-guide':mm});
          widthLabels.push({x:labelX, y, mm, colour});
        } else if (major) {
          svgElement('text', {x,y:firstLabel,'font-size':12,'font-family':'Manrope, system-ui, sans-serif','font-weight':mm===0?700:500,'text-anchor':'middle',fill:'#61666d'},mm);
        }
      }
      // Draw labels last so adjacent guide lines cannot cross their numerals.
      widthLabels.forEach(({x, y, mm, colour}) => {
        svgElement('text', {x,y,'font-size':18,'font-family':'Manrope, system-ui, sans-serif','font-weight':700,'text-anchor':'middle',fill:colour,stroke:'#fff','stroke-width':4,'stroke-linejoin':'round','paint-order':'stroke','data-width-label':mm},mm);
      });
      get('[data-check-line]').style.width = `${CARD_WIDTH * ppm}px`;
      drawStraps();
    }
    function drawStraps() {
      const grid = get('[data-strap-grid]');
      grid.replaceChildren();
      grid.style.setProperty('--dlr-strap-cell', `${Math.max(108, 26 * ppm)}px`);
      for (let mm = 14; mm <= 26; mm++) {
        const figure = document.createElement('figure');
        figure.className = 'dlr__strap-item';
        const shape = document.createElement('div');
        shape.className = 'dlr__strap-shape';
        shape.dataset.strapWidth = String(mm);
        shape.style.width = `${mm * ppm}px`;
        shape.setAttribute('role', 'img');
        shape.setAttribute('aria-label', `${mm} millimetre wide strap. Match the flat top edge to the lug gap.`);
        const shapeWidth = mm * ppm;
        shape.style.height = `${shapeWidth * 3.75}px`;
        // Reuse the Configurator's StitchDiagram long-end outline and seam.
        // Its long axis is shortened for this guide; the lug edge remains exact.
        const schematic = document.createElementNS(NS, 'svg');
        schematic.setAttribute('viewBox', '0 0 48 276');
        schematic.setAttribute('preserveAspectRatio', 'none');
        schematic.setAttribute('aria-hidden', 'true');
        schematic.classList.add('dlr__strap-schematic');
        const add = (parent, tag, attributes) => {
          const element = document.createElementNS(NS, tag);
          Object.entries(attributes).forEach(([key,value]) => element.setAttribute(key,value));
          parent.appendChild(element);
          return element;
        };
        const drawing = add(schematic, 'g', {transform:'matrix(0 1 -1 0 60 -10)'});
        add(drawing, 'path', {d:'M10 12H65C90 12 105 18 126 18H246Q268 18 286 36Q268 54 246 54H126C105 54 90 60 65 60H10Z',fill:'#b18b6c',stroke:'#80634c','stroke-width':1,'data-strap-outline':''});
        const seam = [[22,20],[65,20],[98,23],[126,26],[246,26],[256,28],[266,32],[275,36],[266,40],[256,44],[246,46],[126,46],[98,49],[65,52],[22,52]];
        const segments = seam.slice(1).map((end,i) => ({start:seam[i],end,length:Math.hypot(end[0]-seam[i][0],end[1]-seam[i][1])}));
        const length = segments.reduce((sum,segment) => sum + segment.length,0);
        const count = Math.floor(length / 7);
        const stitching = add(drawing, 'g', {fill:'none',stroke:'#faf7f0','stroke-width':1.7,'stroke-linecap':'round'});
        for (let i = 0; i < count; i++) {
          let distance = (i + .5) * length / count;
          const segment = segments.find((part,index) => {
            if (distance <= part.length || index === segments.length - 1) return true;
            distance -= part.length;
            return false;
          });
          const fraction = distance / segment.length;
          const x = segment.start[0] + fraction * (segment.end[0] - segment.start[0]);
          const y = segment.start[1] + fraction * (segment.end[1] - segment.start[1]);
          const angle = Math.atan2(segment.end[1] - segment.start[1],segment.end[0] - segment.start[0]) - Math.PI / 6;
          const dx = Math.cos(angle) * 1.8, dy = Math.sin(angle) * 1.8;
          add(stitching, 'path', {d:`M${x-dx} ${y-dy}L${x+dx} ${y+dy}`});
        }
        [170,182,194,206,218,230,242].forEach(x => add(drawing, 'circle', {cx:x,cy:36,r:2,fill:'#faf7f0',stroke:'#80634c','stroke-width':.6}));
        add(schematic, 'line', {x1:0,y1:.5,x2:48,y2:.5,stroke:'#80634c','stroke-width':1,'data-strap-top':''});
        shape.appendChild(schematic);
        const label = document.createElement('figcaption');
        label.textContent = `${mm} mm`;
        figure.append(shape, label);
        grid.appendChild(figure);
      }
    }
    function rulerGeometryMatches() {
      if (measurementMode === 'straps') {
        return [...root.querySelectorAll('[data-strap-width]')].every(shape =>
          Math.abs(shape.getBoundingClientRect().width - Number(shape.dataset.strapWidth) * ppm) < 0.75);
      }
      return Math.abs(ruler.getBoundingClientRect().width - Number(ruler.getAttribute('width'))) < 0.75;
    }
    function setMeasurementMode(mode) {
      measurementMode = mode;
      root.querySelectorAll('[data-mode]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.mode === mode)));
      root.querySelectorAll('[data-measure-view]').forEach(panel => { panel.hidden = panel.dataset.measureView !== mode; });
      if (measuring) checkEnvironment();
      trackMeasurement();
    }
    function showStep(step, focus = true) {
      measuring = step === 2;
      get('[data-panel="1"]').hidden = measuring;
      get('[data-panel="2"]').hidden = !measuring;
      root.querySelectorAll('[data-step-label]').forEach(label => {
        if (Number(label.dataset.stepLabel) === step) label.setAttribute('aria-current', 'step');
        else label.removeAttribute('aria-current');
      });
      if (!measuring) updateCard(ppm);
      if (measuring && !rulerGeometryMatches()) {
        invalidate('The measuring scale is being resized by this page. Reset zoom and check your card before measuring.');
        return;
      }
      if (focus) {
        const heading = get(measuring ? '[data-measure-heading]' : '[data-calibration-heading]');
        heading.focus({preventScroll:true});
        (measuring ? heading : root).scrollIntoView({block:'start',behavior:'instant'});
      }
    }
    function invalidate(message) {
      calibration = null;
      showStep(1);
      setNotice(message);
    }
    function checkEnvironment() {
      if (!root.isConnected) return;
      updateDeviceStatus();
      if (measuring && calibration && (changed(calibration, environment()) || !rulerGeometryMatches())) {
        invalidate('Your screen or zoom setting changed. Match your card again before measuring.');
      } else if (!measuring) cardFits();
    }
    slider.addEventListener('pointerdown', () => {
      if (matchMedia('(max-width: 767px)').matches) get('[data-card-stage]').scrollIntoView({block:'start',behavior:'instant'});
    });
    slider.addEventListener('input', () => adjustCard(Number(slider.value)));
    root.querySelectorAll('[data-adjust]').forEach(button => button.addEventListener('click', () => adjustCard(ppm + Number(button.dataset.adjust) * BUTTON_STEP)));
    confirm.addEventListener('click', () => {
      if (!cardFits()) return;
      // Read the rendered edge-to-edge size, including the border, to prevent box-model drift.
      ppm = card.getBoundingClientRect().width / CARD_WIDTH;
      calibration = environment();
      drawRuler();
      get('[data-method]').textContent = 'Calibrated with your card';
      setNotice('');
      showStep(2);
      if (measuring) {
        track('lug_tool_started');
        track('lug_tool_calibrated');
        track('lug_tool_card_calibrated');
        trackMeasurement();
      }
    });
    deviceSelect.addEventListener('change', previewDeviceScale);
    builtIn.addEventListener('change', previewDeviceScale);
    get('[data-use-device]').addEventListener('click', () => {
      const result = updateDeviceStatus();
      if (!result.ok) return;
      ppm = result.ppm;
      calibration = environment();
      drawRuler();
      get('[data-method]').textContent = result.model.name + ' screen preset';
      setNotice('');
      showStep(2);
      if (measuring) {
        track('lug_tool_started');
        track('lug_tool_calibrated');
        track('lug_tool_preset_calibrated');
        trackMeasurement();
      }
    });
    root.querySelectorAll('[data-mode]').forEach(button => button.addEventListener('click', () => setMeasurementMode(button.dataset.mode)));
    get('[data-recalibrate]').addEventListener('click', () => { track('lug_tool_recalibrate'); invalidate(''); });
    window.addEventListener('resize', checkEnvironment);
    window.visualViewport?.addEventListener('resize', checkEnvironment);
    screen.orientation?.addEventListener('change', checkEnvironment);
    window.addEventListener('focus', checkEnvironment);
    document.addEventListener('visibilitychange', checkEnvironment);
    function onPageShow(event) {
      if (event.persisted && measuring) invalidate('Welcome back. Match your card again to check this screen’s scale.');
    }
    window.addEventListener('pageshow', onPageShow);
    let resolutionQuery;
    function watchResolution() {
      resolutionQuery?.removeEventListener('change', onResolutionChange);
      resolutionQuery = matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
      resolutionQuery.addEventListener('change', onResolutionChange);
    }
    function onResolutionChange() { checkEnvironment(); watchResolution(); }
    // Keep support available in the surrounding guide, but off the physical scale.
    const viewportObserver = new IntersectionObserver(entries => {
      root.dataset.inView = String(entries.some(entry => entry.isIntersecting));
    });
    viewportObserver.observe(root);
    const observer = new MutationObserver(() => {
      if (root.isConnected) return;
      window.removeEventListener('resize',checkEnvironment);
      window.removeEventListener('focus',checkEnvironment);
      document.removeEventListener('visibilitychange',checkEnvironment);
      window.removeEventListener('pageshow',onPageShow);
      window.visualViewport?.removeEventListener('resize',checkEnvironment);
      screen.orientation?.removeEventListener('change',checkEnvironment);
      resolutionQuery?.removeEventListener('change',onResolutionChange);
      viewportObserver.disconnect();
      observer.disconnect();
    });
    observer.observe(document.body, {childList:true,subtree:true});
    updateCard(INITIAL_PPM);
    updateDeviceStatus();
    watchResolution();
  }
  const initialiseAll = () => document.querySelectorAll('[data-screen-ruler]').forEach(initialise);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialiseAll);
  else initialiseAll();
  document.addEventListener('shopify:section:load', initialiseAll);
})();
